import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Download, X, Layers, Camera } from "lucide-react";
import axios from "axios";
import { API, BACKGROUNDS, pageVariants } from "../components/app/constants";
import { CreditUpsellModal, PremiumLoader, SocialSharePanel, BeforeAfterSlider, NoiseOverlay } from "../components/shared";
import { SEO } from "../components/shared/SEO";

export const BackgroundPage = ({ user, onUserUpdate, onNavigate }) => {
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedBase64, setUploadedBase64] = useState(null);
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [customPrompt, setCustomPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);

  const compressImage = useCallback((dataUrl) => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const MAX_DIM = 1200;
        let { width, height } = img;
        if (width > MAX_DIM || height > MAX_DIM) {
          if (width > height) { height = Math.round((height * MAX_DIM) / width); width = MAX_DIM; }
          else { width = Math.round((width * MAX_DIM) / height); height = MAX_DIM; }
        }
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = dataUrl;
    });
  }, []);

  const handleFileSelect = useCallback((file) => {
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      const compressed = await compressImage(e.target.result);
      setUploadedImage(compressed);
      setUploadedBase64(compressed.split(",")[1]);
      setResult(null);
    };
    reader.readAsDataURL(file);
  }, [compressImage]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files[0]);
  }, [handleFileSelect]);

  const handleGenerate = async () => {
    if (!selectedBackground || !uploadedBase64) return;
    if (selectedBackground === "custom" && !customPrompt) return;

    if ((user?.credits || 0) < 1) { setShowUpsell(true); return; }

    setGenerating(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/background/replace`, {
        user_id: user.id,
        image_base64: uploadedBase64,
        background_type: selectedBackground,
        custom_prompt: selectedBackground === "custom" ? customPrompt : undefined,
      });
      setResult(res.data);
      onUserUpdate({ ...user, credits: res.data.credits_remaining });
    } catch (err) {
      if (err.response?.status === 402) { setShowUpsell(true); }
      else { alert(err.response?.data?.detail || "Background replacement failed"); }
    }
    setGenerating(false);
  };

  const handleDownload = async () => {
    if (!result?.portrait_id) return;
    try {
      const res = await axios.get(`${API}/portraits/download-hd/${result.portrait_id}?user_id=${user.id}`);
      const link = document.createElement("a");
      link.href = `data:image/png;base64,${res.data.image_base64}`;
      link.download = `starframe-bg-${result.background_type}.png`;
      link.click();
      if (res.data.credits_remaining !== undefined) onUserUpdate({ ...user, credits: res.data.credits_remaining });
    } catch {
      const link = document.createElement("a");
      link.href = `data:image/png;base64,${result.image_base64}`;
      link.download = `starframe-bg-${result.background_type}.png`;
      link.click();
    }
  };

  const isReady = selectedBackground && uploadedBase64 && !generating && (selectedBackground !== "custom" || customPrompt);

  // ─── AUTH GUARD ───
  if (!user) {
    onNavigate("/start");
    return null;
  }

  // ─── RESULT VIEW ───
  if (result) {
    return (
      <motion.div className="min-h-screen bg-[#050505] pt-20 px-4 pb-8" variants={pageVariants} initial="initial" animate="animate" exit="exit">
        <NoiseOverlay />
        <div className="max-w-lg mx-auto space-y-4">
          <h2 className="text-white font-bold text-lg text-center">Before & After</h2>
          <BeforeAfterSlider
            beforeImage={uploadedImage}
            afterImage={`data:image/png;base64,${result.image_base64}`}
            beforeLabel="Original"
            afterLabel="New Background"
          />
          <button data-testid="bg-download-result" onClick={handleDownload}
            className="w-full bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 text-[#D4AF37] font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 border border-[#D4AF37]/30">
            <Download className="w-4 h-4" /> Download HD (1 Credit)
          </button>
          {result.portrait_id && <SocialSharePanel portraitId={result.portrait_id} />}
          <button onClick={() => setResult(null)}
            className="w-full bg-white/5 hover:bg-white/10 text-white/60 py-2.5 rounded-xl transition-colors text-sm">
            Try Another Background
          </button>
        </div>
      </motion.div>
    );
  }

  // ─── GENERATING VIEW ───
  if (generating) {
    return (
      <motion.div className="min-h-screen bg-[#050505] pt-20 flex items-center justify-center" variants={pageVariants} initial="initial" animate="animate" exit="exit">
        <PremiumLoader message="Replacing background..." />
      </motion.div>
    );
  }

  // ─── MAIN FORM ───
  return (
    <motion.div className="min-h-screen bg-[#050505] pt-20 pb-6" variants={pageVariants} initial="initial" animate="animate" exit="exit">
      <NoiseOverlay />
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="text-center py-2 mb-2">
          <h1 className="text-lg md:text-2xl font-bold text-white">Replace Background</h1>
          <p className="text-white/60 text-xs">
            <span className="text-[#D4AF37]">{user?.credits || 0} credits</span> | 1 credit per use
          </p>
        </div>

        {/* Upload — compact */}
        <div
          data-testid="bg-upload-zone"
          className={`border-2 border-dashed rounded-xl text-center transition-all mb-3 ${
            uploadedImage ? "p-2" : "p-3"
          } ${dragOver ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/20"}`}
          onDrop={handleDrop}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
        >
          {uploadedImage ? (
            <div className="relative inline-block">
              <img src={uploadedImage} alt="Uploaded" className="h-16 md:h-24 mx-auto rounded-lg" />
              <button data-testid="bg-remove-upload"
                onClick={() => { setUploadedImage(null); setUploadedBase64(null); setResult(null); }}
                className="absolute -top-1 -right-1 p-1 bg-black/80 rounded-full hover:bg-red-600">
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          ) : (
            <label className="flex items-center justify-center gap-3 cursor-pointer py-1">
              <input type="file" accept="image/*" onChange={e => handleFileSelect(e.target.files[0])} className="hidden" data-testid="bg-file-input" />
              <Camera className="w-5 h-5 text-white/40" />
              <span className="text-white/60 text-sm">Upload your photo</span>
            </label>
          )}
        </div>

        {/* Background Grid — 3 cols mobile, 4 cols tablet+ */}
        <h3 className="text-xs uppercase tracking-wider text-white/70 font-semibold mb-2">Choose Background</h3>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 sm:gap-2 mb-3">
          {BACKGROUNDS.map(bg => (
            <button
              key={bg.id}
              data-testid={`bg-option-${bg.id}`}
              onClick={() => setSelectedBackground(bg.id)}
              className={`p-2 rounded-xl text-center transition-all ${
                selectedBackground === bg.id
                  ? "bg-[#D4AF37]/15 border-2 border-[#D4AF37]"
                  : "bg-white/5 border-2 border-transparent hover:bg-white/10"
              }`}
            >
              <span className="text-lg block mb-0.5">{bg.icon}</span>
              <p className="text-white text-[11px] font-medium leading-tight">{bg.name}</p>
            </button>
          ))}
        </div>

        {/* Custom Background */}
        <button
          data-testid="bg-option-custom"
          onClick={() => setSelectedBackground(selectedBackground === "custom" ? null : "custom")}
          className={`w-full p-2.5 rounded-xl text-left transition-all flex items-center gap-3 mb-3 ${
            selectedBackground === "custom"
              ? "bg-[#D4AF37]/15 border-2 border-[#D4AF37]"
              : "bg-white/5 border-2 border-transparent hover:bg-white/10"
          }`}
        >
          <span className="text-lg">🎨</span>
          <div>
            <p className="text-white font-medium text-sm">Custom Background</p>
            <p className="text-white/40 text-[11px]">Describe any background</p>
          </div>
        </button>

        <AnimatePresence>
          {selectedBackground === "custom" && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden mb-3">
              <input
                type="text"
                data-testid="bg-custom-prompt"
                value={customPrompt}
                onChange={e => setCustomPrompt(e.target.value)}
                placeholder="e.g., A sunset on Mars, Inside a castle..."
                className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-white/40 focus:border-[#D4AF37] focus:outline-none text-xs"
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Generate Button */}
        <button
          data-testid="bg-replace-button"
          onClick={handleGenerate}
          disabled={!isReady}
          className={`w-full font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 text-sm ${
            isReady
              ? "bg-[#D4AF37] hover:bg-[#c9a432] text-black shadow-lg shadow-[#D4AF37]/20"
              : "bg-white/5 border border-white/10 text-white/25 cursor-not-allowed"
          }`}
        >
          <Sparkles className="w-4 h-4" />
          Replace Background (1 Credit)
        </button>
      </div>

      <CreditUpsellModal
        isOpen={showUpsell}
        onClose={() => setShowUpsell(false)}
        onNavigate={onNavigate}
        creditsNeeded={1}
        currentCredits={user?.credits || 0}
      />
    </motion.div>
  );
};
